import { type FC, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { type UserType } from "../../Actions/UserActions";
import BoxForm from "../../Common/BoxForm/BoxForm";
import Button from "../../Common/Button";
import { type RootState } from "../../Reducers/DeveloperReducers";
import { useTitle } from "../../Utilities/TitleHook";
import { PartnerRegistrationBackground } from "./PartnerRegistrationBackground";

class UserAlreadyRegisteredError extends Error {
    constructor() {
        super("User already registered");
        this.name = "UserAlreadyRegisteredError";
    }
}

const PartnerRegistration: FC = () => {
    const user = useSelector<RootState, boolean | UserType>(state => state.user.user);

    const navigate = useNavigate();
    const [ error, setError ] = useState<null | string>(null);
    const [ loading, setLoading ] = useState(false);
    const [ registrationStatus, setRegistrationStatus ] = useState(
        typeof user !== "boolean" ? user.partner_status : "not-registered"
    );

    useTitle("Partner Registration");

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        const form = e.currentTarget;

        const isValid = form.checkValidity();

        setError(isValid ? null : "Please fix the validation errors");
        setLoading(isValid);

        if(!isValid) {
            return;
        }

        const data: Record<string, string> = {};

        for(let i = 0, l = form.elements.length; i < l; i++) {
            const el = form.elements[i] as HTMLInputElement;

            if(!el.name) {
                continue;
            } else if(el.type === "radio" && !el.checked) {
                continue;
            }

            data[el.name] = el.value;
        }

        fetch(form.action, {
            method : "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept"      : "application/json",
            },
            body: JSON.stringify(data),
        })
            .then(async response => {
                if(!response.ok) {
                    if(response.status === 403) {
                        throw new UserAlreadyRegisteredError();
                    }

                    throw await response.json();
                }

                return response.json();
            })
            .then(() => {
                navigate("/partner/registered");
            })
            .catch(err => {
                console.error("Could not submit partner registration", err);

                if(err instanceof UserAlreadyRegisteredError) {
                    setRegistrationStatus("registered");
                } else if(err.message) {
                    setError(err.message);
                } else {
                    setError("Please try again or contact us at api@shopfront.com.au");
                }

                setLoading(false);
            });
    };

    if(typeof user === "boolean") {
        return (
            <main>
                <p>Please log in to register as a partner</p>
            </main>
        );
    }

    let contents;

    if(registrationStatus === "approved") {
        contents = (
            <p>You&apos;ve already been approved as a developer partner.</p>
        );
    } else if(registrationStatus === "registered") {
        contents = (
            <p>
                You&apos;ve already registered to be a partner, we&apos;ll get
                back to you with any feedback or if you&apos;ve been approved.
            </p>
        );
    } else {
        contents = (
            <>
                <p>
                    Please fill in the following information to express interest
                    in becoming a partner.
                </p>
                <form method="post" action="/partner/register" onSubmit={handleSubmit}>
                    <div className="field">
                        <label>
                            Company name
                            <input
                                type="text"
                                name="company"
                                placeholder="Company PTY LTD"
                                required
                                className="p-2 border-2"
                            />
                        </label>
                    </div>
                    <div className="field">
                        <label>
                            Your name
                            <input
                                type="text"
                                name="name"
                                placeholder="John Smith"
                                defaultValue={user.name}
                                required
                                className="p-2 border-2"
                            />
                        </label>
                    </div>
                    <div className="field">
                        <label>
                            Your email
                            <input
                                type="email"
                                name="email"
                                placeholder="john.smith@company.com.au"
                                defaultValue={user.email}
                                required
                                className="p-2 border-2"
                            />
                        </label>
                    </div>
                    <div className="field">
                        <label>
                            Company website
                            <input
                                type="text"
                                name="website"
                                placeholder="https://company.com.au"
                                required
                                className="p-2 border-2"
                            />
                        </label>
                    </div>
                    <div className="field">
                        <label>
                            Company description
                            <textarea
                                name="description"
                                placeholder="Our company develops a range of software including..."
                                required
                                className="p-2 border-2"
                            />
                        </label>
                    </div>
                    <div className="field radio">
                        Do you currently have customers on Shopfront?
                        <label>
                            <input
                                type="radio"
                                value="yes"
                                name="hasCustomers"
                                className="ml-4 mr-2"
                            />Yes
                        </label>
                        <label>
                            <input
                                type="radio"
                                value="no"
                                name="hasCustomers"
                                defaultChecked
                                className="ml-4 mr-2"
                            />No
                        </label>
                    </div>
                    <div className="field radio">
                        Have you already completed your integration with Shopfront?
                        <label>
                            <input
                                type="radio"
                                value="yes"
                                name="completedIntegration"
                                className="ml-4 mr-2"
                            />Yes
                        </label>
                        <label>
                            <input
                                type="radio"
                                value="no"
                                name="completedIntegration"
                                defaultChecked
                                className="ml-4 mr-2"
                            />No
                        </label>
                    </div>
                    <div className="field">
                        <Button type="submit" disabled={loading}>
                            {loading ? "Submitting..." : "Register"}
                        </Button>
                    </div>
                </form>
                <p>
                    Once you&apos;ve registered, Shopfront will review your application
                    and get in touch with you regarding next steps, this may take
                    several business days.
                </p>
            </>
        );
    }

    return (
        <main>
            <PartnerRegistrationBackground />
            <BoxForm
                error={error}
                onClear={() => {
                    setError(null);
                }}
            >
                <div className="partner-registration-form">
                    <h1>Partner Registration</h1>
                    {contents}
                </div>
            </BoxForm>
        </main>
    );
};

export default PartnerRegistration;
